import { z } from 'zod';
import { isoDate, paging, trimmed, uuid } from './common.js';

// ───────────── Notices ─────────────

export const noticeDto = z.object({
  id: uuid,
  title: z.string(),
  body: z.string(),
  category: z.string(),
  audience: z.enum(['all', 'owners', 'tenants']),
  pinned: z.boolean(),
  authorName: z.string().nullable(),
  publishedAt: isoDate,
  expiresAt: isoDate.nullable(),
});
export type NoticeDto = z.infer<typeof noticeDto>;

export const createNotice = z.object({
  title: trimmed(3, 140),
  body: trimmed(3, 5000),
  category: z.enum(['general', 'maintenance', 'event', 'security', 'meeting', 'emergency']).default('general'),
  audience: z.enum(['all', 'owners', 'tenants']).default('all'),
  pinned: z.boolean().default(false),
  expiresAt: z.iso.datetime({ offset: true }).optional(),
});
export const updateNotice = createNotice.partial();

// ───────────── Helpdesk ─────────────

export const ticketStatus = z.enum(['open', 'acknowledged', 'in_progress', 'resolved', 'closed', 'reopened']);
export const ticketPriority = z.enum(['low', 'normal', 'high', 'urgent']);
export type TicketStatus = z.infer<typeof ticketStatus>;

export const categoryDto = z.object({ id: uuid, name: z.string(), slaHours: z.number() });

export const ticketDto = z.object({
  id: uuid,
  number: z.number(),
  title: z.string(),
  description: z.string(),
  status: ticketStatus,
  priority: ticketPriority,
  categoryId: uuid.nullable(),
  categoryName: z.string().nullable(),
  unitId: uuid.nullable(),
  unitLabel: z.string().nullable(),
  createdById: uuid,
  createdByName: z.string().nullable(),
  assignedToId: uuid.nullable(),
  assignedToName: z.string().nullable(),
  slaDueAt: isoDate.nullable(),
  slaBreached: z.boolean(),
  rating: z.number().nullable(),
  attachmentIds: z.array(uuid),
  createdAt: isoDate,
  updatedAt: isoDate,
  resolvedAt: isoDate.nullable(),
});
export type TicketDto = z.infer<typeof ticketDto>;

export const commentDto = z.object({
  id: uuid,
  authorId: uuid,
  authorName: z.string().nullable(),
  body: z.string(),
  internal: z.boolean(),
  createdAt: isoDate,
});

export const ticketDetail = ticketDto.extend({ comments: z.array(commentDto) });

export const createTicket = z.object({
  title: trimmed(3, 140),
  description: trimmed(3, 4000),
  categoryId: uuid.optional(),
  /** Omit for common-area complaints. */
  unitId: uuid.optional(),
  priority: ticketPriority.default('normal'),
});

export const listTicketsQuery = paging.extend({
  status: ticketStatus.optional(),
  mine: z.enum(['true', 'false']).optional(),
  assignedToMe: z.enum(['true', 'false']).optional(),
});

export const ticketListResponse = z.object({ items: z.array(ticketDto), nextCursor: z.string().nullable() });

export const addComment = z.object({ body: trimmed(1, 2000), internal: z.boolean().default(false) });

/** Staff/admin updates. */
export const updateTicket = z.object({
  status: z.enum(['acknowledged', 'in_progress', 'resolved']).optional(),
  priority: ticketPriority.optional(),
  assignedTo: uuid.nullable().optional(),
  categoryId: uuid.nullable().optional(),
});

/** Resident actions on their own ticket. */
export const residentTicketAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('close'), rating: z.number().int().min(1).max(5).optional() }),
  z.object({ action: z.literal('reopen'), reason: trimmed(3, 500) }),
]);

// ───────────── Notifications ─────────────

export const notificationDto = z.object({
  id: uuid,
  type: z.string(),
  title: z.string(),
  body: z.string(),
  data: z.record(z.string(), z.unknown()),
  priority: z.enum(['p0', 'p1', 'p2']),
  readAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type NotificationDto = z.infer<typeof notificationDto>;

export const pushSubscribe = z.discriminatedUnion('platform', [
  z.object({
    platform: z.literal('web'),
    endpoint: z.url().max(1000),
    keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(100) }),
  }),
  z.object({ platform: z.literal('android'), endpoint: z.string().min(20).max(400) }),
]);

/** Messages pushed over the WebSocket. */
export const realtimeEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('notification'), notification: notificationDto }),
  z.object({ type: z.literal('visit.updated'), visitId: uuid, status: z.string() }),
  z.object({ type: z.literal('ticket.updated'), ticketId: uuid }),
]);
export type RealtimeEvent = z.infer<typeof realtimeEvent>;
